import React, { useState, useRef } from "react";
import dayjs from "dayjs";
import RequestEntrySheet, {
  RequestEntrySheetRef,
} from "../Request/RequestEntrySheet";

interface ReportData {
  [day: string]: {
    hrs: number;
    entries: {
      type: string;
      time: string;
      duration?: string;
      accumulated?: string;
      id: number;
    }[];
  };
}

const badgeColor = (type: string): string => {
  switch (type) {
    case "ClockIn":
      return "bg-green-500 text-white";
    case "ClockOut":
      return "bg-red-500 text-white";
    case "BreakOut":
    case "BreakIn":
      return "bg-orange-300 text-black";
    case "LunchOut":
      return "bg-cyan-300 text-black";
    case "LunchIn":
      return "bg-yellow-500 text-white";
    default:
      return "bg-gray-200 text-gray-800";
  }
};

const ReportTable = ({
  data,
  onNotify,
}: {
  data: ReportData;
  onNotify?: (msg: string) => void;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const requestSheetRef = useRef<RequestEntrySheetRef>(null);

  console.log("📋 Entry data:", data);

  const handleEditEntry = (
    entry: { type: string; time: string; id: number },
    day: string
  ) => {
    console.log("📋 Entry selected for edit:", entry);
    console.log("📅 Day:", day);

    const fullTimestamp = `${day} ${entry.time}`; // ejemplo: 2025-05-11 02:24 PM
    const parsedDate = dayjs(fullTimestamp, "YYYY-MM-DD hh:mm A");

    if (!parsedDate.isValid()) {
      console.warn("❌ Invalid date for entry:", entry);
      return;
    }

    requestSheetRef.current?.show(entry.type, parsedDate.toDate(), entry.id);
  };

  if (!data || Object.keys(data).length === 0)
    return <i className="text-gray-500">No report data yet.</i>;

  return (
    <div className="w-full mt-5 border border-gray-200 rounded-md overflow-hidden shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
          <tr>
            <th className="p-3">Day</th>
            <th className="p-3">Total Hours</th>
            <th className="p-3">Edit</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([day, val]) => (
            <React.Fragment key={day}>
              <tr className="border-t border-gray-200 hover:bg-gray-50 transition">
                <td className="p-3 font-medium text-gray-800">
                  <button
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [day]: !prev[day] }))
                    }
                    className="inline-flex items-center gap-1 text-left"
                  >
                    <span className="text-sm">
                      {expanded[day] ? "▼" : "▶"} {day}
                    </span>
                  </button>
                </td>
                <td className="p-3 text-gray-700">
                  {val?.hrs?.toFixed(2)} hrs
                </td>
              </tr>

              {expanded[day] &&
                val.entries.map((entry, i) => (
                  <tr key={`${day}-${i}`} className="border-t border-gray-100">
                    <td className="p-3 pl-6 align-top text-gray-600">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-semibold ${badgeColor(
                            entry.type
                          )}`}
                        >
                          {entry.type}
                        </span>
                        <span className="text-sm text-gray-700 font-medium">
                          {entry.time}
                        </span>
                      </div>
                    </td>

                    <td className="p-3 text-gray-500">
                      {entry.accumulated}
                      {entry.duration && (
                        <div className="text-xs text-gray-400 mt-1">
                          Duration: {entry.duration}
                        </div>
                      )}
                    </td>

                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleEditEntry(entry, day)}
                        className="text-blue-600 hover:underline mr-2 text-xs"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <RequestEntrySheet
        ref={requestSheetRef}
        onSubmit={(data) => {
          console.log("✅ Submitted Correction:", data);
          onNotify?.(
            "Your correction request has been submitted successfully!"
          );
        }}
      />
    </div>
  );
};

export default ReportTable;
